/* Someone, as the phones and the desktop app show them: the person card
 * (who, where they are known from, call, write, the contact, the calls with
 * them) and the after-call panel (how the call went, what to do next;
 * closes after 10 s unless the pointer is on it). */
import { useEffect, useRef, useState } from "preact/hooks";
import { locale, t } from "./i18n";
import { Icon } from "./icon";
import * as S from "./state";
import { Avatar, duration } from "./widgets";

function whenOf(at: number): string {
  const d = new Date(at);
  const today = d.toDateString() === new Date().toDateString();
  return today
    ? d.toLocaleTimeString(locale.value, { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleString(locale.value, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** what to call them: a contact's name, the call's, else the number written as people do */
function titleFor(number: string, name?: string): string {
  const n = S.nameFor(number, name);
  return n === number ? S.prettyNumber(number) : n;
}

function sourceText(c: S.Contact | undefined): string {
  if (!c) return "";
  if (c.source === "directory") return S.directory.value.name || t("person.directory");
  if (c.source === "shared") return c.bookName || t("person.shared");
  return t("person.mine");
}

/** the contact's own actions: create one, or edit it in Contacts */
function ContactButton({ number, name, known }: { number: string; name: string; known?: S.Contact }) {
  if (known?.source === "directory" || known?.source === "shared") return null;
  return (
    <button
      type="button"
      class="btn"
      data-testid="person-contact"
      onClick={() => {
        if (known) {
          S.personNumber.value = "";
          S.afterCall.value = null;
          S.page.value = "contacts";
        } else S.newContactFor(number, name);
      }}
    >
      <Icon name={known ? "edit" : "plus"} size={16} /> {known ? t("person.edit_contact") : t("person.create_contact")}
    </button>
  );
}

export function PersonCard() {
  const number = S.personNumber.value;
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (number && !d.open) d.showModal();
    if (!number && d.open) d.close();
  }, [number]);
  const known = number ? S.whoIs(number) : undefined;
  const calls = number ? S.callsWith(number) : [];
  // nameFor already prefers the contact; a directory entry named by its bare
  // number gives way to the name the calls carried
  const name = titleFor(number, calls.find((c) => !S.notAName(c.name))?.name);
  const line = calls.find((c) => c.account)?.account;
  return (
    <dialog ref={ref} class="person" aria-label={name} onClose={() => (S.personNumber.value = "")} data-testid="person-card">
      {number && (
        <>
          <header class="person-head">
            <Avatar name={name} />
            <div class="grow">
              <h2>{name}</h2>
              <div class="sub">
                {[name !== S.prettyNumber(number) ? S.prettyNumber(number) : "", known?.company && known.company !== name ? known.company : "", sourceText(known)].filter(Boolean).join(" · ")}
              </div>
            </div>
            <button type="button" class="icon-only" aria-label={t("person.close")} title={t("person.close")} onClick={() => (S.personNumber.value = "")}>
              <Icon name="close" size={20} />
            </button>
          </header>
          <div class="person-actions">
            <button
              type="button"
              class="btn success"
              data-testid="person-call"
              onClick={() => {
                S.personNumber.value = "";
                void S.dial(number, line);
              }}
            >
              <Icon name="phone" size={16} /> {t("dialer.call")}
            </button>
            {S.chatEnabled.value && S.isColleague(number) && (
              <button
                type="button"
                class="btn"
                onClick={() => {
                  S.personNumber.value = "";
                  void S.openChatWith(number);
                }}
              >
                <Icon name="chat" size={16} /> {t("chat.write")}
              </button>
            )}
            <ContactButton number={number} name={name} known={known} />
          </div>
          <h3>{t("person.calls")}</h3>
          {calls.length === 0 ? (
            <p class="muted">{t("person.no_calls")}</p>
          ) : (
            <ul class="person-calls">
              {calls.map((c) => (
                <li class={c.missed ? "danger-text" : ""}>
                  <Icon name={c.missed ? "call-missed" : c.dir === "in" ? "call-in" : "call-out"} size={14} />
                  <span>{[whenOf(c.at), c.outcome || (c.missed ? t("history.missed") : ""), c.duration ? duration(c.duration) : ""].filter(Boolean).join(" · ")}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </dialog>
  );
}

export function AfterCall() {
  const row = S.afterCall.value;
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    setTouched(false);
    if (!row) return;
    const id = row.id;
    const timer = setTimeout(() => {
      if (S.afterCall.value?.id === id && !touchedRef.current) S.afterCall.value = null;
    }, 10_000);
    return () => clearTimeout(timer);
  }, [row?.id]);
  const touchedRef = useRef(false);
  touchedRef.current = touched;
  if (!row) return null;
  const known = S.whoIs(row.number);
  const name = titleFor(row.number, row.name);
  const close = () => (S.afterCall.value = null);
  return (
    <aside class="calls-panel after-call" aria-label={t("after.title")} data-testid="after-call" onPointerEnter={() => setTouched(true)}>
      <header>
        <h2>{row.duration > 0 ? t("after.ended") : t("after.no_answer")}</h2>
        <button type="button" class="icon-only" aria-label={t("person.close")} title={t("person.close")} onClick={close}>
          <Icon name="close" size={20} />
        </button>
      </header>
      <button type="button" class="after-who" onClick={() => S.openPerson(row.number)} title={t("after.open_card")}>
        <Avatar name={name} />
        <span class="grow">
          <span class="title">{name}</span>
          <span class="sub">
            {[name !== S.prettyNumber(row.number) ? S.prettyNumber(row.number) : "", row.duration > 0 ? duration(row.duration) : "", S.lines.value.length > 1 && row.account ? S.lineTitle(row.account) : ""].filter(Boolean).join(" · ")}
          </span>
        </span>
      </button>
      <div class="person-actions">
        <button
          type="button"
          class="btn success"
          data-testid="after-call-back"
          onClick={() => {
            close();
            void S.dial(row.number, row.account);
          }}
        >
          <Icon name="phone" size={16} /> {t("after.call_back")}
        </button>
        {S.chatEnabled.value && S.isColleague(row.number) && (
          <button
            type="button"
            class="btn"
            onClick={() => {
              close();
              void S.openChatWith(row.number);
            }}
          >
            <Icon name="chat" size={16} /> {t("chat.write")}
          </button>
        )}
        <ContactButton number={row.number} name={name} known={known} />
      </div>
    </aside>
  );
}
